import { InfoCircleIcon } from '@langchain/untitled-ui-icons';
import LinearProgress from '@mui/joy/LinearProgress';

import { Fragment, useState } from 'react';

import { useOAuthProviders } from '@/Pages/Settings/hooks/useOAuthProviders';
import { Badge, Button, Text, Tooltip } from '@/design-system';
import { usePermissions } from '@/hooks/usePermissions.tsx';
import { useCurrentOrganization } from '@/hooks/useSwr';
import TrashIcon from '@/icons/TrashIcon.svg?react';
import { OAuthProviderSchema } from '@/types/schema';

import { ensureUTCFormatTimestamp } from '../../utils/ensureUTCFormatTimestamp';
import { CreateOAuthProviderModal } from './components/CreateOAuthProviderModal';
import { DeleteOAuthProviderModal } from './components/DeleteOAuthProviderModal';
import { EditOAuthProviderModal } from './components/EditOAuthProviderModal';

function OAuthProvidersTable(props: {
  providers: OAuthProviderSchema[];
  onProviderUpdated: () => void;
}) {
  const { authorize } = usePermissions();
  const [providerToBeDeleted, setProviderToBeDeleted] =
    useState<OAuthProviderSchema | null>(null);

  return (
    <div className="flex flex-col items-stretch rounded-lg border border-secondary">
      <div className="grid grid-cols-[1fr,2fr,2fr,1fr,1fr,auto] items-stretch rounded-lg text-sm">
        <div className="flex h-12 items-center bg-secondary px-5">
          <Text variant="sm" weight="semibold" color="tertiary">
            Provider ID
          </Text>
        </div>
        <div className="flex h-12 items-center bg-secondary px-5">
          <Text variant="sm" weight="semibold" color="tertiary">
            Name
          </Text>
        </div>
        <div className="flex h-12 items-center bg-secondary px-5">
          <Text variant="sm" weight="semibold" color="tertiary">
            Auth URL
          </Text>
        </div>
        <div className="flex h-12 items-center bg-secondary px-5">
          <Text variant="sm" weight="semibold" color="tertiary">
            PKCE
          </Text>
        </div>
        <div className="flex h-12 min-w-0 items-center whitespace-nowrap bg-secondary px-5">
          <Text variant="sm" weight="semibold" color="tertiary">
            Created At
          </Text>
        </div>
        <div className="flex h-12 items-center bg-secondary px-5">
          <Text variant="sm" weight="semibold" color="tertiary">
            Actions
          </Text>
        </div>
        {props.providers?.map((provider) => (
          <Fragment key={provider.id}>
            <div className="flex h-14 items-center gap-2 border-t border-secondary px-5 py-2">
              <code className="font-mono">{provider.provider_id}</code>
            </div>
            <div className="flex h-14 min-w-0 items-center border-t border-secondary px-5 py-2 text-left">
              <span className="shrink overflow-hidden text-ellipsis whitespace-nowrap">
                {provider.name}
              </span>
            </div>
            <div className="flex h-14 min-w-0 items-center border-t border-secondary px-5 py-2 text-left">
              <span className="shrink overflow-hidden text-ellipsis whitespace-nowrap">
                {provider.auth_url}
              </span>
            </div>
            <div className="flex h-14 items-center border-t border-secondary px-5 py-2">
              <Badge color={provider.uses_pkce ? 'success' : 'secondary'}>
                {provider.uses_pkce ? 'Enabled' : 'Disabled'}
              </Badge>
            </div>
            <div className="flex h-14 min-w-0 items-center border-t border-secondary px-5 py-2">
              {provider.created_at != null && (
                <span className="shrink overflow-hidden text-ellipsis whitespace-nowrap">
                  {new Date(
                    ensureUTCFormatTimestamp(provider.created_at)
                  ).toLocaleString()}
                </span>
              )}
            </div>
            <div className="flex h-14 items-center justify-end gap-1 border-t border-secondary px-5 py-2">
              {authorize('workspaces:manage') ? (
                <>
                  <EditOAuthProviderModal
                    provider={provider}
                    onSuccess={props.onProviderUpdated}
                  />
                  <Button
                    type="button"
                    variant="plain"
                    size="sm"
                    aria-label="delete"
                    onClick={() => setProviderToBeDeleted(provider)}
                    className="p-1"
                  >
                    <TrashIcon className="h-5 w-5 text-tertiary" />
                  </Button>
                </>
              ) : null}
            </div>
          </Fragment>
        ))}
      </div>

      {providerToBeDeleted && (
        <DeleteOAuthProviderModal
          isOpen={!!providerToBeDeleted}
          doClose={() => setProviderToBeDeleted(null)}
          providerId={providerToBeDeleted.provider_id}
          providerName={providerToBeDeleted.name}
          onSuccess={props.onProviderUpdated}
        />
      )}
    </div>
  );
}

export const OrganizationOAuthProviders: React.FC = () => {
  const { authorize } = usePermissions();
  const organization = useCurrentOrganization();

  const {
    data: providers,
    isLoading,
    mutate: refetchProviders,
  } = useOAuthProviders({});

  const handleProviderCreated = () => {
    refetchProviders(); // Refresh the list after creation
  };

  if (organization.isLoading) {
    return <LinearProgress />;
  }

  return (
    <div className="space-y-4 overflow-visible">
      <header className="flex items-center justify-between py-4">
        <div className="flex items-center gap-2">
          <Text variant="h1" as="h3">
            OAuth Providers
          </Text>
          <Badge color="secondary" size="md">
            Beta
          </Badge>
          <Tooltip
            title="OAuth Providers"
            description="Configure OAuth providers to enable agents to authenticate with external services on behalf of users."
            side="right"
          >
            <button type="button">
              <InfoCircleIcon className="h-5 w-5 cursor-pointer text-tertiary transition-colors hover:text-secondary" />
            </button>
          </Tooltip>
        </div>

        {authorize('workspaces:manage') && (
          <CreateOAuthProviderModal onSuccess={handleProviderCreated} />
        )}
      </header>

      {!isLoading ? (
        providers && providers.length > 0 ? (
          <OAuthProvidersTable
            providers={providers}
            onProviderUpdated={refetchProviders}
          />
        ) : (
          <div className="rounded-lg border border-secondary py-12 text-center">
            <Text variant="h2" color="secondary" className="mb-4">
              No OAuth providers configured
            </Text>
            <Text variant="sm" color="tertiary" className="mb-6">
              Get started by creating your first OAuth provider to enable agent
              authentication with external services.
            </Text>
          </div>
        )
      ) : (
        <div>
          <LinearProgress />
        </div>
      )}
    </div>
  );
};
